import { Injectable } from '@angular/core';
import { firstValueFrom, forkJoin, from, Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { MasterService } from '../../../master/services/master.service';
import { MasterModel } from '../../../master/models/master.model';
import { PolizaCreditoService } from '../../../../services/finanzas/poliza-credito/poliza-credito.service';
import { InvestmentAssetType } from '../models/investment-asset.model';

export interface InvestmentAssetOption {
  id: any;
  display: string;
  metadata?: any;
}

export interface InvestmentAssetFormLookups {
  accounts: InvestmentAssetOption[];
  taxes: InvestmentAssetOption[];
  contacts: InvestmentAssetOption[];
}

export type InvestmentAssetPayKind = 'bankAccounts' | 'paymentCards' | 'cashRegisters' | 'partners' | 'creditPolicies';

const CREDIT_CARD_TYPE = 1;

@Injectable({ providedIn: 'root' })
export class InvestmentAssetLookupsFacade {

  private readonly payCache = new Map<InvestmentAssetPayKind, Observable<InvestmentAssetOption[]>>();

  constructor(private masterService: MasterService, private polizaCreditoService: PolizaCreditoService) { }

  form(type: InvestmentAssetType): Promise<InvestmentAssetFormLookups> {
    return firstValueFrom(forkJoin({
      accounts: this.masterService.getInvestmentAssetAccounts(type),
      taxes: this.masterService.getTaxes(),
      contacts: this.masterService.getContacts(),
    })).then(result => ({
      accounts: (result.accounts || []).map(item => ({ id: item.id, display: item.display, metadata: item.metadata })),
      taxes: (result.taxes || []).filter(tax => !tax.metadata || tax.metadata.type !== 'AIEM'),
      contacts: this.contactOptions(result.contacts),
    }));
  }

  contacts(): Promise<InvestmentAssetOption[]> {
    return firstValueFrom(this.masterService.getContacts()).then(list => this.contactOptions(list));
  }

  payOptions(kind: InvestmentAssetPayKind): Promise<InvestmentAssetOption[]> {
    let entry = this.payCache.get(kind);
    if (!entry) {
      entry = this.requestPayOptions(kind).pipe(
        catchError(error => {
          this.payCache.delete(kind);
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
      this.payCache.set(kind, entry);
    }
    return firstValueFrom(entry.pipe(map(list => list.slice())));
  }

  private requestPayOptions(kind: InvestmentAssetPayKind): Observable<InvestmentAssetOption[]> {
    switch (kind) {
      case 'bankAccounts':
        return this.masterService.getBankAccounts().pipe(map(list => (list || []).map(item => {
          const bank = item.metadata && item.metadata.bank ? String(item.metadata.bank).trim() : '';
          return { id: item.id, display: bank ? `${bank} · ${item.display}` : item.display };
        })));
      case 'paymentCards':
        return this.masterService.getPaymentCards(CREDIT_CARD_TYPE).pipe(map(list => (list || []).map(item => ({ id: item.id, display: item.display }))));
      case 'cashRegisters':
        return this.masterService.getCashRegisters().pipe(map(list => (list || [])
          .filter(item => item.metadata && item.metadata.id)
          .map(item => ({ id: item.metadata.id, display: item.display }))));
      case 'partners':
        return this.masterService.getParnerts().pipe(map(list => (list || []).map(item => ({ id: Number(item.id), display: item.display }))));
      default:
        return from(this.polizaCreditoService.listPayments()).pipe(
          map((list: any[]) => (list || []).map(item => ({ id: item.uid, display: [item.banco, item.iban].filter(Boolean).join(' · ') || 'Póliza de crédito' }))),
        );
    }
  }

  private contactOptions(list: MasterModel[] | null | undefined): InvestmentAssetOption[] {
    return (list || []).filter(item => !!item.display).map(item => ({ id: item.id, display: item.display }));
  }
}
